import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { environment } from '../../../environments/environment';

declare global {
  interface Window {
    Userback: any;
  }
}

/**
 * Service pour gérer Userback - Collecte de feedback utilisateur
 * 
 * Permet aux utilisateurs de :
 * - Signaler des bugs avec captures d'écran
 * - Proposer des améliorations
 * - Donner leur feedback
 * 
 * @example
 * ```typescript
 * constructor(private userbackService: UserbackService) {}
 * 
 * ngOnInit() {
 *   // Userback se charge automatiquement si activé dans environment
 * }
 * 
 * openFeedback() {
 *   this.userbackService.open();
 * }
 * ```
 */
@Injectable({
  providedIn: 'root'
})
export class UserbackService {
  private isLoaded = false;
  private platformId = inject(PLATFORM_ID);
  private isBrowser = isPlatformBrowser(this.platformId);

  constructor() {
    this.initUserback();
  }
  
  /**
   * Vérifie si on est côté navigateur (pas SSR)
   */
  private isBrowserEnvironment(): boolean {
    return this.isBrowser && typeof window !== 'undefined';
  }

  /**
   * Initialise Userback si activé dans l'environnement
   */
  private initUserback(): void {
    // Ne rien faire côté serveur (SSR)
    if (!this.isBrowserEnvironment()) {
      return;
    }

    // Vérifier si Userback est activé dans l'environnement
    if (!environment.userback?.enabled || !environment.userback?.token) {
      console.log('[Userback] Désactivé ou token manquant');
      return;
    }

    // Charger le script Userback
    this.loadScript();
  }

  /**
   * Charge le script Userback de manière asynchrone
   */
  private loadScript(): void {
    if (!this.isBrowserEnvironment() || this.isLoaded) {
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://static.userback.io/widget/v1.js';
    script.async = true;
    
    script.onload = () => {
      this.isLoaded = true;
      this.configureUserback();
      console.log('[Userback] Widget chargé avec succès');
    };

    script.onerror = () => {
      console.error('[Userback] Erreur lors du chargement du widget');
    };

    document.head.appendChild(script);
  }

  /**
   * Configure Userback avec les paramètres de l'application
   */
  private configureUserback(): void {
    if (!window.Userback) {
      console.error('[Userback] Widget non disponible');
      return;
    }

    window.Userback = window.Userback || {};
    window.Userback.access_token = environment.userback.token;

    // Configuration optionnelle
    window.Userback.widget_settings = {
      ...(environment.userback.settings || {}),
    };
  }

  /**
   * Ouvre le widget Userback
   * Utile pour un bouton "Signaler un bug" personnalisé
   */
  open(): void {
    if (!this.isBrowserEnvironment()) {
      return;
    }

    if (!this.isLoaded) {
      console.warn('[Userback] Widget non chargé');
      return;
    }

    if (window.Userback && typeof window.Userback.open === 'function') {
      window.Userback.open();
    } else {
      console.error('[Userback] Fonction open() non disponible');
    }
  }

  /**
   * Ferme le widget Userback
   */
  close(): void {
    if (!this.isBrowserEnvironment()) {
      return;
    }

    if (window.Userback && typeof window.Userback.close === 'function') {
      window.Userback.close();
    }
  }

  /**
   * Identifie l'utilisateur connecté pour améliorer le suivi
   * 
   * @param user - Informations de l'utilisateur
   * @example
   * ```typescript
   * this.userbackService.identify({
   *   id: '123',
   *   name: 'Marie Dupont',
   *   email: 'marie.dupont@exemple.fr',
   *   role: 'Contributeur'
   * });
   * ```
   */
  identify(user: {
    id: string;
    name?: string;
    email?: string;
    role?: string;
    [key: string]: any;
  }): void {
    if (!this.isBrowserEnvironment()) {
      return;
    }

    if (!this.isLoaded || !window.Userback) {
      console.warn('[Userback] Widget non disponible pour identify()');
      return;
    }

    if (typeof window.Userback.identify === 'function') {
      window.Userback.identify(user.id, {
        name: user.name,
        email: user.email,
        data: {
          role: user.role,
          ...user
        }
      });
      console.log('[Userback] Utilisateur identifié:', user.name);
    }
  }

  /**
   * Désidentifie l'utilisateur (lors de la déconnexion)
   */
  logout(): void {
    if (!this.isBrowserEnvironment()) {
      return;
    }

    if (window.Userback && typeof window.Userback.logout === 'function') {
      window.Userback.logout();
      console.log('[Userback] Utilisateur déconnecté');
    }
  }

  /**
   * Masque le widget Userback
   */
  hide(): void {
    if (!this.isBrowserEnvironment()) {
      return;
    }

    if (window.Userback && typeof window.Userback.hide === 'function') {
      window.Userback.hide();
    }
  }

  /**
   * Affiche le widget Userback
   */
  show(): void {
    if (!this.isBrowserEnvironment()) {
      return;
    }

    if (window.Userback && typeof window.Userback.show === 'function') {
      window.Userback.show();
    }
  }

  /**
   * Enregistre un événement personnalisé
   * 
   * @param eventName - Nom de l'événement
   * @param data - Données additionnelles
   * @example
   * ```typescript
   * this.userbackService.track('commande_created', {
   *   semaine: 42,
   *   nombre_repas: 125
   * });
   * ```
   */
  track(eventName: string, data?: any): void {
    if (!this.isBrowserEnvironment()) {
      return;
    }

    if (window.Userback && typeof window.Userback.track === 'function') {
      window.Userback.track(eventName, data);
      console.log('[Userback] Événement tracké:', eventName, data);
    }
  }
}
